// Static boot scene (visuals §11.1, backlog R3-7, owner decision D-U3): before the first Play or
// Spectate of a page load, agar's canvas shows a fixed decorative scene, not the live arena. Ours
// (original layout, same character): the classic background and 50 u grid at the 1080p scale
// `max(W/1920, H/1080)`, ≈ 25 pellets from a fixed-seed PRNG (none under the centre play card)
// and two big player-style cells cut by the screen edges, with the 10 u band and no names.
// Rendered once per size/theme into a cached bitmap and blitted (no per-frame cost); the dim
// overlay is drawn on top by the renderer.

import { classicOutlineColor, generatedColor, Rng } from '@ballio/shared';
import { classicGridStyle } from './background.js';
import { type Canvas2D, createCanvas, type Ctx2D, get2d } from './canvas.js';
import { BAND_U } from './outline.js';
import { type ArenaTheme, css } from './theme.js';

const TAU = Math.PI * 2;

export const BOOT_SCENE = Object.freeze({
  seed: 0x0ba11,
  pellets: 25,
  pelletMinR: 15,
  pelletMaxR: 25,
  /** The centre play card no pellet sits under: half-size in 1080p CSS px. */
  cardHalfW: 250,
  cardHalfH: 300,
  /** Big cells: radius (u), centre as fractions of the screen (x may be off-screen in u). */
  left: { r: 180, xU: -50, yFrac: 0.6, color: generatedColor(1, 0x5a) },
  right: { r: 115, xFrac: 0.88, yFrac: 0.3, color: generatedColor(2, 0x48) },
  gridU: 50,
});

/** The boot scene's world scale: CSS px per u = max(W/1920, H/1080). */
export function bootSceneScale(cssW: number, cssH: number): number {
  return Math.max(cssW / 1920, cssH / 1080, 1e-3);
}

export interface BootPellet {
  /** Position as fractions of the screen. */
  readonly fx: number;
  readonly fy: number;
  readonly r: number;
  readonly color: number;
}

/** The fixed pellet layout (same on every load); positions avoid the centre card rect. */
export function bootScenePellets(cssW: number, cssH: number): BootPellet[] {
  const rng = new Rng(BOOT_SCENE.seed);
  const s = bootSceneScale(cssW, cssH);
  const out: BootPellet[] = [];
  let guard = 0;
  while (out.length < BOOT_SCENE.pellets && guard++ < 2000) {
    const fx = rng.next();
    const fy = rng.next();
    const r = BOOT_SCENE.pelletMinR + rng.next() * (BOOT_SCENE.pelletMaxR - BOOT_SCENE.pelletMinR);
    const color = generatedColor(rng.int(6), rng.int(256));
    const dx = Math.abs(fx * cssW - cssW / 2);
    const dy = Math.abs(fy * cssH - cssH / 2);
    if (dx < BOOT_SCENE.cardHalfW * s + r * s && dy < BOOT_SCENE.cardHalfH * s + r * s) continue;
    out.push({ fx, fy, r, color });
  }
  return out;
}

/** Paints the boot scene into `ctx` (backing px; `bs` backing px per CSS px). */
export function paintBootScene(
  ctx: Ctx2D,
  cssW: number,
  cssH: number,
  bs: number,
  theme: ArenaTheme,
): void {
  const W = cssW * bs;
  const H = cssH * bs;
  const k = bootSceneScale(cssW, cssH) * bs; // backing px per u
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.fillStyle = css(theme.colors.background);
  ctx.fillRect(0, 0, W, H);
  // Grid: 50 u lines, 1 u wide, anchored at the screen centre.
  const step = BOOT_SCENE.gridU * k;
  ctx.beginPath();
  const cx = W / 2;
  const cy = H / 2;
  for (let x = cx - Math.ceil(cx / step) * step; x <= W; x += step) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, H);
  }
  for (let y = cy - Math.ceil(cy / step) * step; y <= H; y += step) {
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
  }
  ctx.lineWidth = k;
  ctx.strokeStyle = classicGridStyle(theme.dark);
  ctx.stroke();
  for (const p of bootScenePellets(cssW, cssH)) {
    ctx.beginPath();
    ctx.arc(p.fx * W, p.fy * H, p.r * k, 0, TAU);
    ctx.fillStyle = css(p.color);
    ctx.fill();
  }
  const band = (x: number, y: number, r: number, color: number): void => {
    ctx.beginPath();
    ctx.arc(x, y, (r + BAND_U / 2) * k, 0, TAU);
    ctx.fillStyle = css(classicOutlineColor(color));
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, (r - BAND_U / 2) * k, 0, TAU);
    ctx.fillStyle = css(color);
    ctx.fill();
  };
  const L = BOOT_SCENE.left;
  band(L.xU * k, L.yFrac * H, L.r, L.color);
  const R = BOOT_SCENE.right;
  band(R.xFrac * W, R.yFrac * H, R.r, R.color);
}

/** The cached boot scene bitmap (re-rendered only when the size or theme changes). */
export class BootScene {
  private canvas: Canvas2D | null = null;
  private key = '';
  /** Times the bitmap was (re)painted (tests). */
  renders = 0;

  /** Blits the scene over the whole backing store of `ctx`. */
  draw(ctx: Ctx2D, cssW: number, cssH: number, bs: number, theme: ArenaTheme): void {
    const key = `${cssW}x${cssH}@${bs}|${theme.key}`;
    if (key !== this.key || !this.canvas) {
      this.key = key;
      this.canvas = null;
      try {
        const c = createCanvas(Math.round(cssW * bs), Math.round(cssH * bs));
        const g = get2d(c);
        if (g) {
          paintBootScene(g, cssW, cssH, bs, theme);
          this.canvas = c;
          this.renders++;
        }
      } catch {
        this.canvas = null;
      }
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    if (this.canvas) {
      ctx.drawImage(this.canvas, 0, 0);
    } else {
      paintBootScene(ctx, cssW, cssH, bs, theme);
    }
  }

  /** Drops the bitmap (memory once the scene is off for good). */
  release(): void {
    this.canvas = null;
    this.key = '';
  }
}
